/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbSize } from "abreact/misc/AbSize";
import * as React from "react";
import { h2, h3, h4, h5, h6 } from "react-dom-factories";
const AbHeaderContextPrivate = /*#__PURE__*/React.createContext(2);
AbHeaderContextPrivate.displayName = "AbHeaderContext";
export const useHeaderFlavorClass = size => {
  const headerValue = React.useContext(AbHeaderContextPrivate);
  return getHeaderFlavorClass(getHeaderSize(size, headerValue));
};
const getHeaderRenderer = headerValue => {
  switch (headerValue) {
    case 2:
      return h2;
    case 3:
      return h3;
    case 4:
      return h4;
    case 5:
      return h5;
    case 6:
      return h6;
    default:
      throw new Error(`Invalid header number: ${headerValue}. Should be 2-6.`);
  }
};
const getHeaderSize = (size, headerValue) => {
  if (size === undefined) {
    switch (headerValue) {
      case 2:
        return AbSize.EXTRA_LARGE;
      case 3:
        return AbSize.LARGE;
      case 4:
        return AbSize.MEDIUM;
      case 5:
        return AbSize.SMALL;
      case 6:
        return AbSize.EXTRA_SMALL;
      default:
        throw new Error("Invalid header number");
    }
  }
  return size;
};
const getHeaderFlavorClass = size => {
  switch (size) {
    case AbSize.EXTRA_LARGE:
      return "ab-font-heading-xlarge";
    case AbSize.LARGE:
      return "ab-font-heading-large";
    case AbSize.CUSTOM:
    case AbSize.MEDIUM:
      return "ab-font-heading";
    case AbSize.SMALL:
      return "ab-font-heading-small";
    case AbSize.EXTRA_SMALL:
      return "ab-font-heading-xsmall";
  }
};
/**
 * Renders an <h2-6> tag wrapping its children. Should be used approximately one-to-one with an <AbHeaderContext>.
 * For Example:
 * <AbHeader>My Header Text</AbHeader>
 * <AbHeaderContext>
 *   {Lots of stuff under this header}
 * </AbHeaderContext>
 *
 * It is fine to nest header contexts, but is bad form to nest header contexts without headers that correspond to them.
 * For example, do NOT do this:
 * <AbHeader>My Header Text</AbHeader>
 * <AbHeaderContext>
 *   {Lots of stuff under this header, but no additional header}
 *   <AbHeaderContext>
 *     {This context shouldn't exist-- it doesn't have a corresponding header}
 *   </AbHeaderContext>
 * </AbHeaderContext>
 */
export const AbHeader = props => {
  const headerValue = React.useContext(AbHeaderContextPrivate);
  const renderer = getHeaderRenderer(headerValue);
  const headerFlavorClass = useHeaderFlavorClass(props.size);
  const commonProps = ReactUtils.componentProps("ab-header", props, ["abui", headerFlavorClass]);
  const {
    __htmlProps,
    dataTestId,
    ...rest
  } = props;
  return renderer({
    ...rest,
    ...commonProps,
    ...__htmlProps
  });
};
/**
 * Wrap this component around the content associated with a header.
 */
export const AbHeaderContextProvider = props => {
  const headerValue = React.useContext(AbHeaderContextPrivate);
  if (props.disabled) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, props.children);
  }
  const newHeaderValue = Math.min(headerValue + 1, 6);
  return /*#__PURE__*/React.createElement(AbHeaderContextPrivate.Provider, {
    value: newHeaderValue
  }, props.children);
};

/**
 * Used to reset header context. Should only be used in AbPopup for now.
 */
export const AbHeaderRootContext = props => {
  // AbBanner has a hardcoded h1. For the rest of the product, h2 is the largest we want.
  return /*#__PURE__*/React.createElement(AbHeaderContextPrivate.Provider, {
    value: 2
  }, props.children);
};
//# sourceMappingURL=AbHeader.js.map
